import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router"
import * as z from "zod"

import type {
  ExtendedBusinessEntity,
  ExtendedResource,
} from "@repo/supabase/database-types"
import { If } from "@repo/ui/components/ui/if"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { BusinessEntitiesSearchList } from "~/modules/business-entities/components/business-entities-search-list"
import {
  type Pagination,
  ResourcesTable,
} from "~/modules/resources/components/resources-table"
import { getExtendedResourcesQueryOptions } from "~/modules/resources/resources.queries"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const log = logger.child({
  module: "resources-route",
})

const searchSchema = dataTableBaseSearchSchema.extend({
  be_slug: z.string().optional(),
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resources/",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) =>
    pick(search, [
      "be_slug",
      "q",
      "page",
      "page_size",
      "sorting",
      "columnFilters",
    ]),
  loader: async ({ context, deps }) => {
    context.queryClient
      .ensureQueryData(getExtendedBusinessEntitiesQueryOptions())
      .catch((error) => {
        log.error("Error getting extended business entities", { error })
        return null
      })

    const filters = {
      business_entity_slugs: deps.be_slug ? [deps.be_slug] : undefined,
      ...pick(deps, ["q", "page", "page_size", "sorting", "columnFilters"]),
    }

    context.queryClient
      .ensureQueryData(getExtendedResourcesQueryOptions({ filters }))
      .catch((error) => {
        log.error("Error getting extended resources", { error })
        return null
      })

    return {
      filters,
    }
  },
})

function RouteComponent() {
  const { isResourceManagementOn } = useFeatureConfigs()

  if (!isResourceManagementOn) {
    throw notFound()
  }

  const isMobile = useIsMobile()
  const { filters } = Route.useLoaderData()
  const router = useRouter()

  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )
  const { data: resourcesData } = useQuery(
    getExtendedResourcesQueryOptions({
      filters,
    }),
  )

  const selectedBusinessEntitySlug = filters.business_entity_slugs?.[0]

  const selectedBusinessEntity = businessEntitiesData.businessEntities.find(
    (be) => be.slug === selectedBusinessEntitySlug,
  )

  const onSelectResource = (resource: ExtendedResource) => {
    router.navigate({
      to: "/resources/$resourceSlug/overview",
      params: { resourceSlug: resource.slug },
      search: (prev) => prev,
    })
  }

  const onSelectBusinessEntity = (
    businessEntity: ExtendedBusinessEntity | null,
  ) => {
    router.navigate({
      to: ".",
      search: (prev) => ({ ...prev, be_slug: businessEntity?.slug }),
    })
  }

  const pagination: Pagination = {
    total: resourcesData?.count ?? 0,
    page: filters.page,
    page_size: filters.page_size,
  }

  return (
    <PagePanel title="Resources">
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile && selectedBusinessEntitySlug,
          })}
        >
          <BusinessEntitiesSearchList
            businessEntities={businessEntitiesData.businessEntities}
            onSelectBusinessEntity={onSelectBusinessEntity}
            selectedBusinessEntitySlug={selectedBusinessEntitySlug}
          />
        </div>

        <div
          className={cn("block flex-1 md:w-2/3 lg:w-3/4", {
            hidden: isMobile && !selectedBusinessEntitySlug,
          })}
        >
          <div className="h-full flex-1 overflow-auto">
            <If
              condition={resourcesData}
              fallback={
                <div className="h-full flex-1 overflow-auto">
                  <div className="flex h-64 items-center justify-center">
                    <div className="text-white/70">Loading resources...</div>
                  </div>
                </div>
              }
            >
              {(data) => (
                <ResourcesTable
                  resources={data.resources}
                  onSelectResource={onSelectResource}
                  selectedBusinessEntity={selectedBusinessEntity}
                  pagination={pagination}
                  initialColumnFilters={filters.columnFilters}
                  initialSorting={filters.sorting}
                />
              )}
            </If>
          </div>
        </div>
      </div>
    </PagePanel>
  )
}
